import { AuthenticationResult } from '@azure/msal-browser';
import axios, { AxiosInstance } from 'axios';
import Discount from '../Models/Discount';
import AuthService from './AuthService';

class ApiService {

    // constructor that requires a base URI.
    constructor(baseUri: string, authService: AuthService) {
        if (!baseUri) {
            throw new Error('the base uri was not provided');
        }
        if (!authService) {
            throw new Error('the auth service was not provided');
        }

        this.AuthorizationService = authService;

        this.AuthenticatedApi = axios.create({
            baseURL: baseUri,
            // will inject auth header on-demand later as needed.
            headers: { 
                'Content-Type': 'application/json',
            }
        });
    }

    // the authenticated api
    AuthenticatedApi: AxiosInstance;

    // the authorization service
    // wrapper around MSAL
    AuthorizationService: AuthService;

    InvokeOfcomApiCall() {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get('/Report?reportType=Ofcom', {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response;
                })
                .catch((error: any) => {
                    throw Error('An error has occurred calling the web api: ' + error);
                });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    InvokeWaitlistApiCall(email: string) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get('/Report?reportType=Waitlist&email=' + email, {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response;
                })
                .catch((error: any) => {
                    throw Error('An error has occurred calling the web api: ' + error);
                });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    InvokeUpgradeApiCall(email: string) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get('/Report?reportType=Upgrade&email=' + email, {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response;
                })
                .catch((error: any) => {
                    throw Error('An error has occurred calling the web api: ' + error);
                });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    InvokeServiceExpiryApiCall(email: string) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get('/Report?reportType=ServiceExpiry&email=' + email, {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response;
                })
                .catch((error: any) => {
                    throw Error('An error has occurred calling the web api: ' + error);
                });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    InvokeAddressApiCall(localPopIds: string) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get('/Report?reportType=Address&data=' + localPopIds, {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response;
                })
                .catch((error: any) => {
                    throw Error('An error has occurred calling the web api: ' + error);
                });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    InvokeTariffListApiCall() {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get('/Tariff', {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response.data;
                })
                .catch((error: any) => {
                    throw Error('An error has occurred calling the web api: ' + error);
                });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    InvokeLocalPopListApiCall() {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get('/LocalPop', {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response.data;
                })
                .catch((error: any) => {
                    throw Error('An error has occurred calling the web api: ' + error);
                });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    InvokeDiscountListApiCall() {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get('/Discount', {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response.data;
                })
                .catch((error: any) => {
                    throw Error('An error has occurred calling the web api: ' + error);
                });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    InvokeDiscountDeleteApiCall(id: number) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.delete('/Discount?id=' + id, {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response.data;
                })
                .catch((error: any) => {
                    throw Error('An error has occurred calling the web api: ' + error);
                });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    InvokeDiscountUpdateApiCall(discount: Discount) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.put('/Discount', discount, {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response.data;
                })
                .catch((error: any) => {
                    throw Error('An error has occurred calling the web api: ' + error);
                });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    InvokeDiscountCreateApiCall(discount: Discount) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.post('/Discount', discount, {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response.data;
                })
                .catch((error: any) => {
                    throw Error('An error has occurred calling the web api: ' + error);
                });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }
}

export default ApiService;